import { createPortal } from 'react-dom';
import { X, ExternalLink } from 'lucide-react';

interface TikTokEmbedModalProps {
  tiktokId: string;
  username?: string;
  onClose: () => void;
}

/**
 * Full-screen TikTok video embed modal.
 * Renders via portal to document.body to escape any CSS containment.
 * On mobile, the iframe uses max-h-[70vh] so the close button stays visible.
 * TikTok embed handles autoplay and sound based on user interaction and browser policies.
 */
export default function TikTokEmbedModal({ tiktokId, username, onClose }: TikTokEmbedModalProps) {
  // Build embed URL - let TikTok embed handle playback with sound on user interaction
  const embedUrl = `https://www.tiktok.com/embed/v2/${tiktokId}`;
  
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/70 hover:text-white transition z-10"
        >
          <X className="w-8 h-8" />
        </button>

        {/* TikTok Embed iframe — constrained on mobile so X button is reachable */}
        <div
          className="rounded-xl overflow-hidden shadow-2xl bg-black w-full max-h-[70vh] sm:max-h-none"
          style={{ aspectRatio: '9/16' }}
        >
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            style={{ border: 'none' }}
          />
        </div>

        {/* Open in TikTok link */}
        <a
          href={`https://www.tiktok.com${username ? `/@${username}` : ''}/video/${tiktokId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 mt-4 text-sm text-slate-300 hover:text-white transition"
        >
          <ExternalLink className="w-4 h-4" />
          Open in TikTok
        </a>
      </div>
    </div>,
    document.body
  );
}
